import React from "react";
import { MapPin, Clock, Truck, MonitorPlay, Phone } from "@phosphor-icons/react";

const OFFICES = [
  {
    name: "TechConnect LLC – Clayton",
    street: "5720 US 70 Bus Hwy, Suite B",
    cityLine: "Clayton, NC 27520",
    phone: "(919) 737-5442",
    phoneTel: "+19197375442",
    serves: ["Clayton", "Smithfield", "Garner", "Raleigh", "Knightdale", "Wendell", "Fuquay-Varina", "Cary", "Durham", "Chapel Hill"],
  },
  {
    name: "TechConnect LLC – Granite Falls",
    street: "10A Falls Avenue",
    cityLine: "Granite Falls, NC 28630",
    phone: "(919) 737-5442",
    phoneTel: "+19197375442",
    serves: ["Granite Falls", "Hickory", "Lenoir", "Morganton", "Newton", "Conover"],
  },
];

export default function ServiceArea() {
  return (
    <section id="service-area" data-testid="service-area-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">Coverage</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
              Two offices. All of North Carolina.
            </h2>
            <p className="mt-4 text-lg text-zinc-600 leading-relaxed">
              On-site IT support from our Clayton (Triangle) and Granite Falls (Foothills) offices.
              Remote support available nationwide, resolving many issues same-day without an on-site visit.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {OFFICES.map((o) => (
                <div
                  key={o.name}
                  data-testid={`office-${o.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5"
                >
                  <div className="font-heading text-lg font-bold text-zinc-900">{o.name}</div>
                  <address className="mt-2 not-italic text-sm text-zinc-700 leading-relaxed">
                    {o.street}<br />
                    {o.cityLine}
                  </address>
                  <a
                    href={`tel:${o.phoneTel}`}
                    className="mt-3 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
                    data-testid={`office-phone-${o.cityLine.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Phone size={14} weight="duotone" /> {o.phone}
                  </a>
                  <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                      Serves
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {o.serves.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-0.5 text-xs"
                        >
                          <MapPin size={10} weight="fill" /> {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-5 text-sm">
              <div className="flex items-center gap-2 text-zinc-700">
                <Clock size={18} weight="duotone" className="text-blue-600" />
                Mon–Sat, 8am – 8pm
              </div>
              <div className="flex items-center gap-2 text-zinc-700">
                <Truck size={18} weight="duotone" className="text-blue-600" />
                Free pickup & delivery in service area
              </div>
              <div className="flex items-center gap-2 text-zinc-700">
                <MonitorPlay size={18} weight="duotone" className="text-blue-600" />
                Remote support available nationwide
              </div>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden border border-zinc-200 aspect-[4/3] bg-zinc-900">
            <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full">
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                </pattern>
                <radialGradient id="coverage" cx="50%" cy="50%" r="55%">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity="0.55" />
                  <stop offset="60%" stopColor="#2563EB" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                </radialGradient>
              </defs>
              <rect width="400" height="300" fill="#09090B" />
              <rect width="400" height="300" fill="url(#grid)" />
              <path
                d="M 30 175 C 60 165, 110 158, 150 155 C 200 152, 240 148, 280 145 C 320 142, 355 138, 380 130 L 380 175 C 360 180, 340 190, 320 200 C 280 215, 240 215, 200 210 C 160 205, 120 205, 80 198 C 60 195, 40 190, 30 185 Z"
                fill="url(#coverage)"
                stroke="#2563EB"
                strokeWidth="1.5"
                strokeOpacity="0.6"
              />
              {[
                [95, 168, "Granite Falls"],
                [125, 178, "Hickory"],
                [175, 158, "Greensboro"],
                [240, 158, "Durham"],
                [255, 162, "Raleigh"],
                [270, 172, "Clayton"],
              ].map(([cx, cy, label], i) => (
                <g key={label}>
                  <circle cx={cx} cy={cy} r="4" fill="#3B82F6" />
                  <circle cx={cx} cy={cy} r="9" fill="#3B82F6" opacity="0.25">
                    <animate attributeName="r" values={`9;${14 + (i % 3)};9`} dur="2.6s" repeatCount="indefinite" begin={`${i * 0.15}s`} />
                    <animate attributeName="opacity" values="0.35;0;0.35" dur="2.6s" repeatCount="indefinite" begin={`${i * 0.15}s`} />
                  </circle>
                </g>
              ))}
              <text x="200" y="265" textAnchor="middle" fill="#71717a" fontSize="12" fontWeight="500" letterSpacing="3">
                NORTH CAROLINA
              </text>
            </svg>
            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-lg px-4 py-3 border border-zinc-200">
              <div className="text-xs font-semibold uppercase tracking-widest text-blue-600">Coverage</div>
              <div className="text-sm font-semibold text-zinc-900 mt-0.5">Two NC offices · Remote support nationwide</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
